import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import type { FilterReunionDto } from '../../types/reuniones';
import { REUNION_TIPO_VALUES, REUNION_ESTADO_VALUES } from '../../utils/reuniones';
import { useCentros } from '../../hooks/useCentros'; // <-- Importar hook

export interface StaffOption {
  id: number;
  nombre?: string;
  nombreCompleto?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

export interface AlumnoOption {
  id: number;
  nombreCompleto?: string;
  nombre?: string;
  email?: string;
  [key: string]: any;
}

interface CentroOption {
  id: number;
  nombre: string;
}

interface FiltrosCalendarioProps {
  onFilterChange: (filters: FilterReunionDto) => void;
  staffOptions: StaffOption[];
  alumnoOptions: AlumnoOption[];
}

const FiltrosCalendario: React.FC<FiltrosCalendarioProps> = ({
  onFilterChange,
  staffOptions,
  alumnoOptions,
}) => {
  const [filters, setFilters] = useState<FilterReunionDto>({});
  const [fechaDesde, setFechaDesde] = useState<Dayjs | null>(null);
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(null);

  // Obtener centros
  const { rows: centros = [] } = useCentros();
  const centroOptions: CentroOption[] = useMemo(() => {
    return centros.map((c) => ({ id: c.id, nombre: c.nombre }));
  }, [centros]);

  useEffect(() => {
  const updated: FilterReunionDto = {};

  // IDs numéricos (solo si son números válidos)
  if (Number.isFinite(Number(filters.idStaff))) {
    updated.idStaff = Number(filters.idStaff);
  }

  if (Number.isFinite(Number(filters.idAlumno))) {
    updated.idAlumno = Number(filters.idAlumno);
  }

  if (Number.isFinite(Number(filters.idCentro))) {
    updated.idCentro = Number(filters.idCentro);
  }

  // Strings
  if (filters.tipo) updated.tipo = filters.tipo;
  if (filters.estado) updated.estado = filters.estado;
  if (filters.origen) updated.origen = filters.origen;
  if (filters.search) updated.search = filters.search;

  // Fechas
  if (fechaDesde) updated.fechaDesde = fechaDesde.toISOString();
  if (fechaHasta) updated.fechaHasta = fechaHasta.toISOString();

  onFilterChange(updated);
}, [filters, fechaDesde, fechaHasta, onFilterChange]);

 const handleFilterChange = (key: keyof FilterReunionDto, value: any) => {
  setFilters((prev) => {
    const next = { ...prev };

    if (value === '' || value == null) {
      delete next[key]; 
    } else {
      next[key] = value;
    }

    return next;
  });
};
  const handleClearFilters = () => {
    setFilters({});
    setFechaDesde(null);
    setFechaHasta(null);
  };

  const activeFilterCount = useMemo(() => {
    const baseCount = Object.entries(filters).filter(([, v]) => v !== undefined && v !== '').length;
    return baseCount + (fechaDesde ? 1 : 0) + (fechaHasta ? 1 : 0);
  }, [filters, fechaDesde, fechaHasta]);

  const selectedStaff = useMemo(
    () => staffOptions.find((opt) => opt.id === filters.idStaff) ?? null,
    [staffOptions, filters.idStaff],
  );

  const selectedAlumno = useMemo(
    () => alumnoOptions.find((opt) => opt.id === filters.idAlumno) ?? null,
    [alumnoOptions, filters.idAlumno],
  );

  const selectedCentro = useMemo(
    () => centroOptions.find((opt) => opt.id === filters.idCentro) ?? null,
    [centroOptions, filters.idCentro],
  );

  const getStaffLabel = (o: StaffOption) =>
    o?.nombre ?? o?.nombreCompleto ?? o?.email ?? String(o?.id ?? '');

  const getAlumnoLabel = (o: AlumnoOption) =>
    o?.nombreCompleto ?? o?.nombre ?? o?.email ?? String(o?.id ?? '');

  const getCentroLabel = (o: CentroOption) => o?.nombre ?? String(o?.id ?? '');

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: '#21a598 !important' },
      '&:hover fieldset': { borderColor: '#21a598 !important' },
    },
    '& .MuiInputLabel-root': {
      '&.Mui-focused': { color: '#21a598 !important' },
    },
  } as const;

  const listBoxSx = {
    fontSize: '0.875rem',
    maxHeight: 250,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '& .MuiAutocomplete-option': {
      '&[aria-selected="true"], &[aria-selected="true"].Mui-focusVisible, &[aria-selected="true"]:hover':
        { backgroundColor: '#fcf1ad !important', color: '#000 !important' },
      '&:not([aria-selected="true"]):hover, &:not([aria-selected="true"]).Mui-focusVisible': {
        backgroundColor: '#fcf1ad !important',
        color: '#000 !important',
      },
      '&:not([aria-selected="true"])': {
        backgroundColor: 'transparent',
        color: '#000',
      },
    },
  } as const;

  return (
    <Box sx={{ width: '100%' }}>
      {activeFilterCount > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`Filtros activos: ${activeFilterCount}`} color="primary" size="small" />
          <Button size="small" onClick={handleClearFilters} variant="outlined">
            Limpiar filtros
          </Button>
        </Stack>
      )}

      <Grid container spacing={2}>
        {/* STAFF */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={staffOptions}
            value={selectedStaff}
            getOptionLabel={getStaffLabel}
            isOptionEqualToValue={(o, v) => o.id === (v?.id ?? v)}
            onChange={(_, v) => handleFilterChange('idStaff', (v as StaffOption | null)?.id)}
            renderInput={(params) => (
              <TextField {...params} label="Staff" placeholder="Buscar staff..." sx={inputSx} />
            )}
            ListboxProps={{ sx: listBoxSx }}
          />
        </Grid>

        {/* ALUMNO */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={alumnoOptions}
            value={selectedAlumno}
            getOptionLabel={getAlumnoLabel}
            isOptionEqualToValue={(o, v) => o.id === (v?.id ?? v)}
            onChange={(_, v) => handleFilterChange('idAlumno', (v as AlumnoOption | null)?.id)}
            renderInput={(params) => (
              <TextField {...params} label="Alumno" placeholder="Buscar alumno..." sx={inputSx} />
            )}
            ListboxProps={{ sx: listBoxSx }}
          />
        </Grid>

        {/* CENTRO */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={centroOptions}
            value={selectedCentro}
            getOptionLabel={getCentroLabel}
            isOptionEqualToValue={(o, v) => o.id === (v?.id ?? v)}
            onChange={(_, v) => handleFilterChange('idCentro', (v as CentroOption | null)?.id)}
            renderInput={(params) => (
              <TextField {...params} label="Centro" placeholder="Buscar centro..." sx={inputSx} />
            )}
            ListboxProps={{ sx: listBoxSx }}
          />
        </Grid>

        {/* TIPO */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filters.tipo ?? ''}
              label="Tipo"
              onChange={(e) => handleFilterChange('tipo', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {REUNION_TIPO_VALUES.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* ESTADO */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select
              value={filters.estado ?? ''}
              label="Estado"
              onChange={(e) => handleFilterChange('estado', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {REUNION_ESTADO_VALUES.map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estado.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* FECHA DESDE */}
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Fecha desde"
            value={fechaDesde}
            onChange={(value) => setFechaDesde(value)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>

        {/* FECHA HASTA */}
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Fecha hasta"
            value={fechaHasta}
            minDate={fechaDesde ?? undefined}
            onChange={(value) => setFechaHasta(value)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>

        {/* BUSCAR EN COMENTARIOS */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Buscar en comentarios"
            value={filters.search ?? ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            size="small"
          />
        </Grid>

        {/* ORIGEN */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Origen</InputLabel>
            <Select
              value={filters.origen ?? ''}
              label="Origen"
              onChange={(e) => handleFilterChange('origen', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="pasarela_ventas">Pasarela Ventas</MenuItem>
              <MenuItem value="otro">Otro</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FiltrosCalendario;
